import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { CircleIcon, SquareIcon } from 'lucide-react'
import { usePreviewMode } from '@/components/custom/route-transition/surface/route-preview'
import { rtEvents } from '@/components/custom/route-transition/core/debug'
import { TransitionTabsSurface } from './-components/shared/transition-tabs'
import { setCommitCost, useCommitCost } from './-components/dev/transition-playground'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// THE FULL-CAPABILITY transition playground — the app's real mobile topology, in miniature and with no data:
//
//   OUTER LATERAL   this layout: a two-member pager (One / Two), like the top-level section pager
//   HIERARCHICAL    inside One: a two-level nav stack, like a section
//   INNER LATERAL   inside the pushed screen: a four-tab pager, like the report tabs, Board as heavy as Tablero
//
// The tab strips are the SAME component the report renders (shared/transition-tabs.tsx). The HUD shows stack
// depth, both pagers' positions, a GLITCH counter and the engine's recent events frozen at that moment.
export const Route = createFileRoute('/_app/dev-resources/transitions')({
  component: RouteComponent,
})

export const TRANSITIONS_SCOPE = '/dev-resources/transitions'

/**
 * IS THIS NODE IN THE LIVE ROUTE TREE, or inside one of the pager's preview panes?
 *
 * A pager's neighbour slots each mount a shadow router, and level 0 renders its WHOLE stack in one, so whenever a
 * preview is mounted the document holds more than one transitions stack. The live one is inside the pager's
 * `data-rt-slot="current"`; previews live in the inert `prev`/`next` slots, and an unscoped `.first()` measures a
 * preview. No slot ancestor at all means nothing is paging this subtree, which is the live tree too.
 */
function inLiveTree(el: HTMLElement): boolean {
  const slot = el.closest('[data-rt-slot]')
  return slot === null || slot.getAttribute('data-rt-slot') === 'current'
}

/**
 * IS ANY PARKED LEVEL ON THE STAGE? The stack's own invariant, asked of the live DOM every frame.
 *
 * Every level that is not one of the seam's two sides is a whole screen of live DOM that must be somewhere the
 * stage cannot show, and `visibility` is not an answer: it is paint-time, so a late repaint or a stale compositor
 * texture draws a "hidden" level anyway. The BOX is. Previews are scanned too, each judged against its OWN box,
 * since a preview pane is on screen for the whole travel. (The on-page half of `expectSound`, at 60Hz.)
 */
function parkedOnStage(): string | null {
  for (const stage of document.querySelectorAll<HTMLElement>(`[data-rt-surface^="${TRANSITIONS_SCOPE}"] .ns-stage`)) {
    const box = stage.getBoundingClientRect()
    if (box.width === 0 || box.height === 0) continue
    // ONLY ONCE THE FLAGS PASS HAS RUN — the same `[data-ns-live]` guard the hiding rule itself carries. Before
    // it, nothing is flagged either side of the seam ON PURPOSE: that is the pre-hydration paint, where the
    // levels are placed by the URL-derived rule alone and the root is correctly the one on screen. Judging that
    // paint by the flags reads every startup frame as a violation (measured: 10 of them, all at depth 0).
    if (!stage.hasAttribute('data-ns-live')) continue
    for (const layer of stage.querySelectorAll<HTMLElement>('[data-ns-layer]')) {
      if (layer.closest('.ns-stage') !== stage) continue
      if (layer.hasAttribute('data-ns-active') || layer.hasAttribute('data-ns-under')) continue
      const r = layer.getBoundingClientRect()
      const overlaps =
        r.right > box.left + 1 && r.left < box.right - 1 && r.bottom > box.top + 1 && r.top < box.bottom - 1
      if (overlaps) {
        const depth = getComputedStyle(stage).getPropertyValue('--ns-depth').trim()
        const flags = ['live', 'moving', 'gesture', 'armed', 'adopted']
          .filter((flag) => stage.hasAttribute(`data-ns-${flag}`))
          .join('|')
        const tree = inLiveTree(stage) ? '' : 'preview '
        return `${tree}L${layer.style.getPropertyValue('--ns-i')} at x${Math.round(r.x)} y${Math.round(r.y)} · d${depth} [${flags}]`
      }
    }
  }
  return null
}

/**
 * The on-page glitch detector across every pager here, plus the stack's two state invariants:
 *   • `glitches` — MOTION: a pager may only travel the way a live drag pulls it, so any pull-back > 18px from
 *     the gesture's extremum is counted, the rtlog buffer frozen alongside.
 *   • `parked` — STATE: a level neither side of the seam, overlapping the stage; it never moves, which is why
 *     the motion detector never saw it.
 *   • `defer` — how long the stack has been on a level the URL has not caught up with (`data-ns-adopted`).
 */
function TransitionHud() {
  const cost = useCommitCost()
  const [view, setView] = useState({ line: '—', glitches: 0, parked: 0, defer: 0, peak: 0, events: [] as string[] })
  const frozen = useRef<string[] | null>(null)
  useEffect(() => {
    let raf = 0
    let touchDown = false
    let glitches = 0
    let parked = 0
    let deferSince = 0
    let deferPeak = 0
    let lastText = ''
    const tracks = new Map<string, { anchor: number | null; dir: number }>()
    const pagers = () =>
      [...document.querySelectorAll<HTMLElement>('.rt-scroller')].flatMap((el) => {
        if (el.clientWidth === 0 || el.closest('[inert]')) return []
        const surface = el.closest('[data-rt-surface]')?.getAttribute('data-rt-surface') ?? ''
        return surface.startsWith(TRANSITIONS_SCOPE) ? [{ el, surface }] : []
      })
    const dump = () => rtEvents().map((e) => `#${e.n} ${e.t} ${e.evt}${e.data ? ' ' + JSON.stringify(e.data) : ''}`)
    const onTouch = (e: TouchEvent) => {
      touchDown = e.touches.length > 0
      if (!touchDown) tracks.clear()
    }
    document.addEventListener('touchstart', onTouch, { passive: true, capture: true })
    document.addEventListener('touchend', onTouch, { passive: true, capture: true })
    document.addEventListener('touchcancel', onTouch, { passive: true, capture: true })
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const found = pagers()
      const parts: string[] = []
      // THE LIVE STACK'S, never a preview's: level 0 renders its whole stack into the pager's neighbour slots
      // too (see inLiveTree), and the two numbers below — the depth cue and the deferred window — are single
      // values the page is read for. A preview's stage sits earlier in the DOM whenever one is mounted, so an
      // unscoped `querySelector` would report a shadow router's resting depth as the stack's.
      const stage = [
        ...document.querySelectorAll<HTMLElement>(`[data-rt-surface="${TRANSITIONS_SCOPE}"] [data-ns-stage]`),
      ].find(inLiveTree)
      if (stage) parts.push(`d${getComputedStyle(stage).getPropertyValue('--ns-depth').trim() || '0'}`)
      for (const { el, surface } of found) {
        const m = new DOMMatrixReadOnly(getComputedStyle(el).transform)
        const v = el.scrollLeft - m.e
        const w = el.clientWidth || 1
        const label = surface === TRANSITIONS_SCOPE ? 'outer' : surface.endsWith('/alpha') ? 'sub' : 'inner'
        parts.push(`${label} ${(v / w).toFixed(2)}`)
        if (touchDown) {
          const track = tracks.get(surface) ?? { anchor: null, dir: 0 }
          tracks.set(surface, track)
          if (track.anchor === null) track.anchor = v
          if (track.dir === 0) {
            if (Math.abs(v - track.anchor) > 6) track.dir = Math.sign(v - track.anchor)
          } else {
            track.anchor = track.dir > 0 ? Math.max(track.anchor, v) : Math.min(track.anchor, v)
            if (track.dir * (v - track.anchor) < -18) {
              glitches += 1
              frozen.current = dump()
              track.anchor = v
              track.dir = 0
            }
          }
        }
      }
      // THE STATE INVARIANT, every frame. Counted, not just shown: the artefact it catches is five frames long
      // and stands perfectly still, so anything that only reads a live value misses it as reliably as the eye
      // does. A hit freezes the event dump next to it, exactly as a motion glitch does.
      const violation = parkedOnStage()
      if (violation !== null) {
        parked += 1
        frozen.current = [`PARKED ON STAGE — ${violation}`, ...dump()]
      }
      // …and the window it becomes dangerous in: the stack is on a level the URL has not caught up with.
      const adopting = stage?.hasAttribute('data-ns-adopted') ?? false
      if (adopting && deferSince === 0) deferSince = performance.now()
      const deferFor = adopting ? Math.round(performance.now() - deferSince) : 0
      if (!adopting) deferSince = 0
      deferPeak = Math.max(deferPeak, deferFor)

      const text = `${parts.join(' · ')}·g${glitches}·p${parked}·${deferFor}/${deferPeak}`
      if (text !== lastText) {
        lastText = text
        setView({
          line: parts.join(' · '),
          glitches,
          parked,
          defer: deferFor,
          peak: deferPeak,
          events: (frozen.current ?? dump()).slice(-5),
        })
      }
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('touchstart', onTouch, { capture: true })
      document.removeEventListener('touchend', onTouch, { capture: true })
      document.removeEventListener('touchcancel', onTouch, { capture: true })
    }
  }, [])
  return (
    <div
      data-rt-hud=""
      data-rt-parked={view.parked}
      className="fixed bottom-2 left-2 z-50 max-w-[95vw] rounded bg-black/85 p-2 font-mono text-[10px] leading-tight text-white"
    >
      <div className="pointer-events-none">
        <span className="text-cyan-300">{view.line}</span> · glitches{' '}
        <span className={view.glitches > 0 ? 'font-bold text-red-400' : 'text-green-400'}>{view.glitches}</span> ·
        parked <span className={view.parked > 0 ? 'font-bold text-red-400' : 'text-green-400'}>{view.parked}</span> ·
        defer{' '}
        <span className="text-amber-300">
          {view.defer}/{view.peak}ms
        </span>
      </div>
      {/* THE COMMIT COST — the knob that makes the deferred window real (see burnCommitCost). A pop's navigation
          lands a task after the stack has handed over, so how long the popped level stays rendered on top of
          the new one is exactly how long its route takes to commit. At 0 this page cannot reproduce a phone. */}
      <div className="mt-1 flex items-center gap-1">
        <span className="text-white/60">commit cost</span>
        {[0, 60, 200].map((ms) => (
          <Button
            key={ms}
            variant="ghost"
            size="sm"
            onClick={() => setCommitCost(ms)}
            className={cn(
              'h-4 rounded px-1 font-mono text-[10px]',
              cost === ms ? 'bg-white text-black' : 'bg-white/20',
            )}
          >
            {ms}ms
          </Button>
        ))}
      </div>
      {view.events.map((line) => (
        <div key={line} className="max-w-[90vw] truncate text-white/60">
          {line}
        </div>
      ))}
    </div>
  )
}

function RouteComponent() {
  // Previews route through here bare (the HUD and the outer strip are live-tree chrome); the tab surface
  // itself gates its own previews, but the HUD must not render into preview panes either — and that now
  // includes the `above` case: the DEV-RESOURCES tab pager (dev-resources.tsx) previews this whole page as one
  // of its panes, so this subtree is ordinary content inside someone else's pane. The playground renders
  // normally there (its own strip IS part of the previewed page), but a second copy of a `fixed` HUD is not
  // content — it would sit over the live one, reading the live tree's pagers. See usePreviewMode's `above`.
  const preview = usePreviewMode(TRANSITIONS_SCOPE)
  if (preview === 'passthrough') return <Outlet />
  return (
    <div className="flex min-h-[70vh] flex-col">
      <TransitionTabsSurface
        scope={TRANSITIONS_SCOPE}
        // PROTOTYPE, and the playground is deliberately the only surface that turns it on: the arriving tab is
        // touchable before the transition has finished — tap a button on it during the settle, or catch a
        // settling pager and tap instead of dragging. See `interactiveIncoming` for the rule and its costs.
        //
        // A native scroll owns every touch until it stops moving, so this only unlocks the window after the snap
        // has LANDED and before the router has committed — see scroll-transition-outlet's header.
        interactiveIncoming
        tabs={[
          { segment: 'one', label: 'One (stack)', icon: SquareIcon },
          { segment: 'two', label: 'Two', icon: CircleIcon },
        ]}
      />
      {preview === 'live' ? <TransitionHud /> : null}
    </div>
  )
}
